import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";

import { SearchPageCard } from "../../molecules/search-page-card";
import { SvgIconHandler } from "../../atoms/svg-icon-hadler";
import SearchIcon from "../../../../assets/icons/searchicon1";
import { navigateTo } from "../../../arrow/utils/utils";
import DateIcon from "../../atoms/date-icon";
import { DateFilter } from "../../molecules/date-filter";
import { RelevanceDropdown } from "../../molecules/relevance-dropdown";
import { SectionFilter } from "../../molecules/section-filter";
import FilterIcon from "../../atoms/filter-icon";
import { Pagination } from "../../atoms/Pagination";
import LoadingIndicatorComponent from "../../atoms/loading-indicator";
import { filterStoriesByForum } from "../../../utils/forum-filter";
import { getSearchPageItems } from "../../../../api/utils";

import "./search.m.css";
import CloseIcon from "../../atoms/CloseIcon";

const SearchPage = (props) => {
  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const isAuthInitializing = useSelector((state) => get(state, ["isAuthInitializing"], false));
  // If user is logged in but subscriberMetadata is null, it's still loading
  const isMetadataLoading = member && subscriberMetadata === null;

  // State for managing all fetched stories and pagination
  const [allFetchedStories, setAllFetchedStories] = useState(props.data.stories || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState(props.data.query || "");

  // Apply client-side forum filtering to all fetched stories
  const filteredStories = useMemo(() => {
    if (!allFetchedStories || allFetchedStories.length === 0) return [];
    return filterStoriesByForum(allFetchedStories, member, subscriberMetadata);
  }, [allFetchedStories, member, subscriberMetadata]);

  // Calculate stories to show on current page
  const storiesPerPage = 10;
  const currentPageStories = useMemo(() => {
    const startIndex = (currentPage - 1) * storiesPerPage;
    const endIndex = startIndex + storiesPerPage;
    return filteredStories.slice(startIndex, endIndex);
  }, [filteredStories, currentPage, storiesPerPage]);

  const dispatch = useDispatch();


const getDefaultDateFilter = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  return {
    startMonth: "1",
    startYear: "2023",
    endMonth: currentMonth.toString(),
    endYear: currentYear.toString()
  };
};

const timestampToDateFilter = (timestamp) => {
  const date = new Date(timestamp);
  return {
    month: (date.getMonth() + 1).toString(),
    year: date.getFullYear().toString()
  };
};

const getInitialDateFilter = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const publishedAfter = urlParams.get('publishedAfter');
    const publishedBefore = urlParams.get('publishedBefore');
    
    if (publishedAfter && publishedBefore) {
      const startDate = timestampToDateFilter(parseInt(publishedAfter));
      const endDate = timestampToDateFilter(parseInt(publishedBefore));
      
      return {
        startMonth: startDate.month,
        startYear: startDate.year,
        endMonth: endDate.month,
        endYear: endDate.year
      };
    }
  }
  return getDefaultDateFilter();
};

  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showSectionFilter, setShowSectionFilter] = useState(false);
  const [selectedSections, setSelectedSections] = useState(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const sectionIds = urlParams.get('section-id');
      return sectionIds ? sectionIds.split(',') : [];
    }
    return [];
  });
  const [dateFilter, setDateFilter] = useState(getInitialDateFilter());
  const [urlSearchParams, setUrlSearchParams] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.location.search;
    }
    return '';
  });

  useEffect(() => {
    if (showDateFilter || showSectionFilter) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    };
  }, [showDateFilter, showSectionFilter]);

  useEffect(() => {
    const newDateFilter = getInitialDateFilter();
    setDateFilter(newDateFilter);
  }, [urlSearchParams]);

  const [sortBy, setSortBy] = useState(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('sort') || "score";
    }
    return 'score';
  });

  // Add useEffect to fetch more stories when needed
  useEffect(() => {
    const fetchMoreStories = async () => {
      if (isLoadingMore) return;

      const currentStoriesEndIndex = currentPage * storiesPerPage;

      // If we need more stories for the current page
      if (currentStoriesEndIndex >= filteredStories.length && allFetchedStories.length < props.data.total) {
        setIsLoadingMore(true);
        try {
          const newOffset = allFetchedStories.length;
          const batchSize = 10;
          const loadMoreStories = await getSearchPageItems(
            searchQuery,
            newOffset,
            batchSize,
            getFirstDayOfMonthTimestamp(dateFilter.startYear, dateFilter.startMonth),
            getLastDayOfMonth(dateFilter.endYear, dateFilter.endMonth),
            sortBy,
            selectedSections
          );

          if (loadMoreStories && loadMoreStories.length > 0) {
            setAllFetchedStories(prev => [...prev, ...loadMoreStories.map(item => item.story)]);
          }
        } catch (error) {
          console.error('Error fetching more stories:', error);
        } finally {
          setIsLoadingMore(false);
        }
      }
    };

    fetchMoreStories();
  }, [currentPage]); // Only depend on currentPage to avoid infinite loops

  const collection = {
    items: currentPageStories?.map(storyWrapper => storyWrapper?.type === "story" ? storyWrapper?.story : storyWrapper),
  };

  const handlePageChange = (page) => {
    setCurrentPage(page); // The useEffect will handle fetching more stories if needed
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleSearchInput = async (e) => {
    if (e.key === 'Enter') {
      const query = searchQuery.trim();
      if(!query.length) return;
      const params = new URLSearchParams();
      params.append('q', query);
      navigateTo(dispatch, `/search?${params.toString()}`);
    }
  }

  const getFirstDayOfMonthTimestamp = (year, month) =>  {
    return new Date(year, month - 1, 1).getTime();
  }

  const getLastDayOfMonth = (year, month) => {
    return new Date(year, month, 0).getTime();
  }

  const handleDateFilterApply = async (newDateFilter) => {
    setDateFilter(newDateFilter);
    setCurrentPage(1);
    setIsLoadingMore(true);

    // Reset fetched stories and fetch new ones with date filter
    try {
      const freshStories = await getSearchPageItems(
        searchQuery,
        0, // Start from beginning
        50, // Fetch more initially to account for filtering
        getFirstDayOfMonthTimestamp(newDateFilter.startYear, newDateFilter.startMonth),
        getLastDayOfMonth(newDateFilter.endYear, newDateFilter.endMonth),
        sortBy,
        selectedSections
      );

      // Reset stories with new filtered results
      setAllFetchedStories(freshStories ? freshStories.map(item => item.story) : []);

      const params = new URLSearchParams();
      params.append('q', searchQuery.trim());

      if (newDateFilter.startMonth && newDateFilter.startYear) {
        const afterTimestamp = getFirstDayOfMonthTimestamp(newDateFilter.startYear, newDateFilter.startMonth)
        params.append('publishedAfter', String(afterTimestamp));
      }
      if (newDateFilter.endMonth && newDateFilter.endYear) {
        const beforeTimestamp = getLastDayOfMonth(newDateFilter.endYear, newDateFilter.endMonth)
        params.append('publishedBefore', String(beforeTimestamp));
      }
      if (selectedSections && selectedSections.length > 0) {
        params.append('section-id', selectedSections.join(','));
      }
      if (sortBy) {
        params.append('sort', sortBy);
      }

      const newUrl = `/search?${params.toString()}`;
      setUrlSearchParams(params.toString());

      // Update URL without full page refresh
      window.history.pushState({}, '', newUrl);
    } catch (error) {
      console.error('Error applying date filter:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleDateFilterClose = () => {
    setShowDateFilter(false);
  };

  const toggleDateFilter = () => {
    setShowDateFilter(!showDateFilter);
  };

  const toggleSectionFilter = () => {
    setShowSectionFilter(!showSectionFilter);
  };

  const handleSectionFilterApply = async (newSelectedSections) => {
    setSelectedSections(newSelectedSections);
    setCurrentPage(1);
    setIsLoadingMore(true);

    // Reset fetched stories and fetch new ones with section filter
    try {
      const freshStories = await getSearchPageItems(
        searchQuery,
        0, // Start from beginning
        50, // Fetch more initially to account for filtering
        getFirstDayOfMonthTimestamp(dateFilter.startYear, dateFilter.startMonth),
        getLastDayOfMonth(dateFilter.endYear, dateFilter.endMonth),
        sortBy,
        newSelectedSections
      );

      // Reset stories with new filtered results
      setAllFetchedStories(freshStories ? freshStories.map(item => item.story) : []);

      // Build new URL with section filter
      const params = new URLSearchParams();
      params.append('q', searchQuery.trim());

      if (dateFilter.startMonth && dateFilter.startYear) {
        const afterTimestamp = getFirstDayOfMonthTimestamp(dateFilter.startYear, dateFilter.startMonth)
        params.append('publishedAfter', String(afterTimestamp));
      }
      if (dateFilter.endMonth && dateFilter.endYear) {
        const beforeTimestamp = getLastDayOfMonth(dateFilter.endYear, dateFilter.endMonth)
        params.append('publishedBefore', String(beforeTimestamp));
      }
      if (newSelectedSections && newSelectedSections.length > 0) {
        params.append('section-id', newSelectedSections.join(','));
      }
      if (sortBy) {
        params.append('sort', sortBy);
      }

      const newUrl = `/search?${params.toString()}`;
      setUrlSearchParams(params.toString());

      // Update URL without full page refresh
      window.history.pushState({}, '', newUrl);
    } catch (error) {
      console.error('Error applying section filter:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleSectionFilterClose = () => {
    setShowSectionFilter(false);
  };

  const handleSortChange = async (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1);
    setIsLoadingMore(true);

    // Reset fetched stories and fetch new ones with new sort
    try {
      const freshStories = await getSearchPageItems(
        searchQuery,
        0, // Start from beginning
        50, // Fetch more initially to account for filtering
        getFirstDayOfMonthTimestamp(dateFilter.startYear, dateFilter.startMonth),
        getLastDayOfMonth(dateFilter.endYear, dateFilter.endMonth),
        newSort,
        selectedSections
      );

      // Reset stories with new sorted results
      setAllFetchedStories(freshStories ? freshStories.map(item => item.story) : []);

      // Trigger search with new sort
      const params = new URLSearchParams();
      params.append('q', searchQuery.trim());

      if (dateFilter.startMonth && dateFilter.startYear) {
        const afterTimestamp = getFirstDayOfMonthTimestamp(dateFilter.startYear, dateFilter.startMonth)
        params.append('publishedAfter', String(afterTimestamp));
      }
      if (dateFilter.endMonth && dateFilter.endYear) {
        const beforeTimestamp = getLastDayOfMonth(dateFilter.endYear, dateFilter.endMonth)
        params.append('publishedBefore', String(beforeTimestamp));
      }
      if (selectedSections && selectedSections.length > 0) {
        params.append('section-id', selectedSections.join(','));
      }
      if (newSort) {
        params.append('sort', newSort);
      }

      const newUrl = `/search?${params.toString()}`;
      setUrlSearchParams(params.toString());

      // Update URL without full page refresh
      window.history.pushState({}, '', newUrl);
    } catch (error) {
      console.error('Error changing sort:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const totalPages = Math.ceil(filteredStories.length / storiesPerPage);

  return (
    <div className="container" styleName="search-page">
      <h1 styleName="page-title">Search result</h1>
      
      <div styleName="search-bar">
        <div styleName="search-input-wrapper">
          <div styleName="search-icon">
            <SearchIcon />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchInput}
            placeholder="e.g. executive briefings"
            styleName="search-input"
          />
          {searchQuery && (
            <button onClick={clearSearch} styleName="clear-button" aria-label="Clear search">
              <CloseIcon color="#8F8F8F" strokeWidth="1.25" />
            </button>
          )}
        </div>
      </div>
      {(isAuthInitializing || isMetadataLoading || isLoadingMore) ? (
        <LoadingIndicatorComponent />
      ) : (
        <>
          <div styleName={`results-header ${filteredStories.length === 0 ? 'no-border' : ''}`}>
            <p styleName="results-count">{filteredStories.length} results for “{props.data.query}”</p>
          {filteredStories.length !== 0 && <div styleName="filters">
            <div styleName="section-filter">
              <button
                styleName={`filter-button ${selectedSections.length > 0 ? 'active' : ''}`}
                onClick={toggleSectionFilter}
                disabled={selectedSections.length === 0 && filteredStories.length <= 0}
              >
                <FilterIcon />
                Filters
              </button>
              <SectionFilter
                key={`section-filter-${selectedSections.join('-')}`}
                isVisible={showSectionFilter}
                onClose={handleSectionFilterClose}
                onApply={handleSectionFilterApply}
                initialSelectedSections={selectedSections}
              />
            </div>
            <div styleName="date-filter">
              <button
              styleName="filter-button active"
              onClick={toggleDateFilter}
              >
                <DateIcon />
                Date
              </button>
              <DateFilter
                key={`date-filter-${JSON.stringify(dateFilter)}`}
                isVisible={showDateFilter}
                onClose={handleDateFilterClose}
                onApply={handleDateFilterApply}
                initialDateFilter={dateFilter}
              />
            </div>
            
            <RelevanceDropdown 
              onSortChange={handleSortChange}
              currentSort={sortBy}
            />
          </div>}
          </div>

          {filteredStories.length <= 0 && (
            <div>
              <p styleName="suggestion-text">Try searching for something else, e.g. <strong>"executive briefings"</strong></p>
            </div>
          )}

          <div styleName="results-list">
            {collection.items.map((story) => (
              <SearchPageCard key={story.id} story={story} />
            ))}
          </div>

          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

SearchPage.propTypes = {
  data: PropTypes.object,
};

export { SearchPage };
